/**
 * ShopBirdy Galaxy PDP runtime.
 *
 * This module is intentionally isolated from the shared ProductController. The
 * Galaxy section owns variant and bundle state, while the existing delegated
 * `[data-product-form]` handler remains responsible for Shopify AJAX cart
 * submission and refreshing `<studio-cart-drawer>`.
 */
(() => {
  const RUNTIME_KEY = "__shopbirdyGalaxyRuntime";
  const existingRuntime = window[RUNTIME_KEY];
  if (existingRuntime) {
    existingRuntime.boot(document);
    return;
  }

  const EVENT_STATE = "studio-galaxy:state-change";
  const EVENT_STATE_REQUEST = "studio-galaxy:state-request";
  const EVENT_SELECT_VARIANT = "studio-galaxy:select-variant";
  const EVENT_SELECT_BUNDLE = "studio-galaxy:select-bundle";
  const EVENT_VARIANT_CHANGE = "studio-galaxy:variant-change";
  const EVENT_BUNDLE_CHANGE = "studio-galaxy:bundle-change";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const productControllers = new Set();
  const galleryControllers = new Set();
  const sceneControllers = new Set();
  const beforeAfterControllers = new Set();
  const revealControllers = new Set();
  const faqBindings = new WeakSet();
  const videoBindings = new WeakSet();
  const trackedVideos = new WeakSet();

  function isDesignMode() {
    return Boolean(window.Shopify?.designMode || document.body?.classList.contains("is-design-mode"));
  }

  function allWithin(scope, selector) {
    const matches = [];
    if (scope instanceof Element && scope.matches(selector)) matches.push(scope);
    if ("querySelectorAll" in scope) matches.push(...scope.querySelectorAll(selector));
    return matches;
  }

  function lastIdPart(value) {
    const stringValue = String(value ?? "").trim();
    return stringValue.includes("/") ? stringValue.split("/").pop() || "" : stringValue;
  }

  function cents(value) {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "string" && value.includes(".")) {
      const decimal = Number(value);
      return Number.isFinite(decimal) ? Math.round(decimal * 100) : 0;
    }
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number) : 0;
  }

  function number(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function escapeSelector(value) {
    if (window.CSS?.escape) return window.CSS.escape(String(value));
    return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }

  function formatMoney(value, format) {
    const safe = Number.isFinite(value) ? Math.round(value) : 0;
    const moneyFormat = format || document.body?.dataset.moneyFormat || "";
    const token = moneyFormat.match(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/i);

    if (!token) {
      const currency = window.Shopify?.currency?.active || "USD";
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en", {
          style: "currency",
          currency,
        }).format(safe / 100);
      } catch {
        return `$${(safe / 100).toFixed(2)}`;
      }
    }

    const kind = token[1].toLowerCase();
    const decimals = kind.includes("no_decimals") ? 0 : 2;
    const decimalSeparator = kind.includes("comma_separator") ? "," : ".";
    let thousandsSeparator = ",";
    if (kind.includes("comma_separator")) thousandsSeparator = ".";
    else if (kind.includes("space_separator")) thousandsSeparator = " ";
    else if (kind.includes("apostrophe_separator")) thousandsSeparator = "'";

    const fixed = (safe / 100).toFixed(decimals);
    const [whole = "0", fraction] = fixed.split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    const display = fraction === undefined ? grouped : `${grouped}${decimalSeparator}${fraction}`;
    return moneyFormat.replace(token[0], display);
  }

  function pushDataLayer(eventName, payload) {
    window.dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : [];
    window.dataLayer.push({ event: eventName, ...payload });
  }

  function track(eventName, payload = {}) {
    const eventPayload = {
      page_path: window.location.pathname,
      ...payload,
    };
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== "function") {
      pushDataLayer(eventName, eventPayload);
      return;
    }

    let fellBack = false;
    const fallback = () => {
      if (fellBack) return;
      fellBack = true;
      pushDataLayer(eventName, eventPayload);
    };

    try {
      const result = publish.call(window.Shopify.analytics, eventName, eventPayload);
      if (result === false) fallback();
      else if (result && typeof result.catch === "function") result.catch(fallback);
    } catch {
      fallback();
    }
  }

  function normalizeVariant(raw) {
    const image = raw.featured_image || raw.featuredImage || null;
    const media = raw.featured_media || raw.featuredMedia || null;
    const options = Array.isArray(raw.options)
      ? raw.options.map(String)
      : Array.isArray(raw.selectedOptions)
        ? raw.selectedOptions.map((option) => String(option.value ?? option.name ?? ""))
        : [];
    return {
      id: lastIdPart(raw.id),
      title: String(raw.title || options.join(" / ") || "Version"),
      options,
      available: raw.availableForSale ?? raw.available !== false,
      price: cents(typeof raw.price === "object" ? raw.price?.amount : raw.price),
      compareAtPrice: cents(
        typeof (raw.compare_at_price ?? raw.compareAtPrice) === "object"
          ? (raw.compare_at_price ?? raw.compareAtPrice)?.amount
          : (raw.compare_at_price ?? raw.compareAtPrice),
      ),
      mediaId: lastIdPart(media?.id ?? raw.media_id ?? raw.mediaId),
      image: image
        ? {
            src: String(image.src || image.url || ""),
            alt: String(image.alt || image.altText || ""),
          }
        : null,
      raw,
    };
  }

  function parseVariants(root) {
    const script = root.querySelector("[data-galaxy-variants], script[data-product-variants]");
    if (script?.textContent?.trim()) {
      try {
        const parsed = JSON.parse(script.textContent);
        const variants = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed.variants)
            ? parsed.variants
            : Array.isArray(parsed.nodes)
              ? parsed.nodes
              : Array.isArray(parsed.variants?.nodes)
                ? parsed.variants.nodes
                : [];
        const normalized = variants.map(normalizeVariant).filter((variant) => variant.id);
        if (normalized.length > 0) return normalized;
      } catch {
        // Invalid editor JSON should not break the native product form.
      }
    }

    return allWithin(root, "[data-galaxy-variant-card]")
      .map((card) => {
        const input = card.matches("input") ? card : card.querySelector("input");
        return normalizeVariant({
          id: card.dataset.variantId || input?.value,
          title: card.dataset.variantTitle || card.dataset.variantValue || card.dataset.friendlyName,
          available: card.dataset.available !== "false" && !input?.disabled,
          price: card.dataset.price,
          compare_at_price: card.dataset.compareAtPrice,
          media_id: card.dataset.mediaId,
          featured_image: card.dataset.imageSrc
            ? { src: card.dataset.imageSrc, alt: card.dataset.imageAlt || "" }
            : null,
        });
      })
      .filter((variant) => variant.id);
  }

  function variantTraits(variant) {
    const value = `${variant?.title || ""} ${(variant?.options || []).join(" ")}`.toLowerCase();
    const bluetooth = /bluetooth|\bbt\b/.test(value);
    const lightBody = /black\s*(?:and|&|\+)\s*white|b\s*(?:and|&)\s*w|\bwhite\b/.test(value);
    return {
      bluetooth,
      color: lightBody ? "White" : /\bblack\b/.test(value) ? "Black" : "",
      version: bluetooth ? "bluetooth" : "standard",
    };
  }

  function friendlyVariantName(variant, preferred = "") {
    if (preferred.trim()) return preferred.trim();
    const traits = variantTraits(variant);
    if (traits.color) return `${traits.bluetooth ? "Bluetooth" : "Standard"} ${traits.color}`;
    return variant?.title || "Choose a version";
  }

  function productIdFor(element) {
    if (!(element instanceof Element)) return "";
    const own = element.dataset.productId || element.dataset.galaxyProductId;
    if (own) return lastIdPart(own);
    const scope = element.closest("[data-galaxy-product-id], [data-product-id], studio-galaxy-product");
    return lastIdPart(scope?.dataset.productId || scope?.dataset.galaxyProductId || "");
  }

  function elementBelongsToProduct(element, productId) {
    const targetId = productIdFor(element);
    return !targetId || !productId || targetId === productId;
  }

  function textTarget(element) {
    return (
      element.querySelector(
        "[data-galaxy-submit-label], [data-galaxy-button-label], [data-sticky-label], [data-add-label]",
      ) || null
    );
  }

  function inputForCard(card) {
    if (!(card instanceof Element)) return null;
    if (card instanceof HTMLInputElement) return card;
    const nested = card.querySelector("input");
    if (nested instanceof HTMLInputElement) return nested;
    if (card instanceof HTMLLabelElement) {
      if (card.control instanceof HTMLInputElement) return card.control;
      if (card.htmlFor) {
        const associated = document.getElementById(card.htmlFor);
        if (associated instanceof HTMLInputElement) return associated;
      }
    }
    const sibling = card.previousElementSibling;
    return sibling instanceof HTMLInputElement ? sibling : null;
  }

  function setVisible(element, visible) {
    element.hidden = !visible;
    element.setAttribute("aria-hidden", String(!visible));
    if ("inert" in element) element.inert = !visible;
  }

  class GalaxyProductController {
    constructor(root) {
      this.root = root;
      this.productId = lastIdPart(root.dataset.productId || root.dataset.galaxyProductId || "");
      this.moneyFormat = root.dataset.moneyFormat || document.body?.dataset.moneyFormat || "";
      this.form =
        root.querySelector("form[data-galaxy-form][data-product-form]") ||
        root.querySelector("form[data-galaxy-form]") ||
        root.querySelector("form[data-product-form]") ||
        root.querySelector('form[action*="/cart/add"]');
      if (this.form && !this.form.hasAttribute("data-product-form")) {
        this.form.setAttribute("data-product-form", "");
      }
      this.variantInput =
        this.form?.querySelector("[data-galaxy-variant-id]") || this.form?.querySelector('input[name="id"]') || null;
      this.quantityInput =
        this.form?.querySelector("[data-galaxy-quantity]") || this.form?.querySelector('input[name="quantity"]') || null;
      this.addButton =
        this.form?.querySelector("[data-add-to-cart]") ||
        this.form?.querySelector('button[type="submit"][name="add"]') ||
        null;
      this.variants = parseVariants(root);
      this.variant = null;
      this.quantity = 1;
      this.discountPercent = 0;
      this.lastSubmitPlacement = "main";
      this.lastButtonState = this.addButton?.dataset.state || "idle";
      this.buttonObserver = null;
      this.stickyObserver = null;

      this.handleRootClick = this.handleRootClick.bind(this);
      this.handleRootChange = this.handleRootChange.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleStateRequest = this.handleStateRequest.bind(this);
      this.handleExternalVariant = this.handleExternalVariant.bind(this);
      this.handleExternalBundle = this.handleExternalBundle.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connect() {
      if (this.root.dataset.galaxyProductReady === "true") return;
      this.root.dataset.galaxyProductReady = "true";
      this.root.addEventListener("click", this.handleRootClick);
      this.root.addEventListener("change", this.handleRootChange);
      this.form?.addEventListener("submit", this.handleSubmit);
      document.addEventListener("click", this.handleDocumentClick);
      document.addEventListener(EVENT_STATE_REQUEST, this.handleStateRequest);
      document.addEventListener(EVENT_SELECT_VARIANT, this.handleExternalVariant);
      document.addEventListener(EVENT_SELECT_BUNDLE, this.handleExternalBundle);

      const bundle = this.initialBundle();
      this.quantity = bundle.quantity;
      this.discountPercent = bundle.discountPercent;
      this.variant = this.initialVariant();
      this.observeButtonState();
      this.setupStickyBuy();
      this.render(false);
      this.publishState("initial");
    }

    disconnect() {
      this.root.removeEventListener("click", this.handleRootClick);
      this.root.removeEventListener("change", this.handleRootChange);
      this.form?.removeEventListener("submit", this.handleSubmit);
      document.removeEventListener("click", this.handleDocumentClick);
      document.removeEventListener(EVENT_STATE_REQUEST, this.handleStateRequest);
      document.removeEventListener(EVENT_SELECT_VARIANT, this.handleExternalVariant);
      document.removeEventListener(EVENT_SELECT_BUNDLE, this.handleExternalBundle);
      this.buttonObserver?.disconnect();
      this.stickyObserver?.disconnect();
      this.root.removeAttribute("data-galaxy-product-ready");
    }

    initialVariant() {
      const requested = new URL(window.location.href).searchParams.get("variant");
      const checked = this.root.querySelector(
        '[data-galaxy-variant-card] input:checked, input[data-galaxy-variant-input]:checked',
      );
      const selectedCard = this.root.querySelector(
        '[data-galaxy-variant-card][data-selected="true"], [data-galaxy-variant-card][aria-pressed="true"]',
      );
      const requestedId = lastIdPart(
        requested || checked?.value || checked?.dataset.variantId || selectedCard?.dataset.variantId || this.variantInput?.value,
      );
      return (
        this.variants.find((variant) => variant.id === requestedId) ||
        this.variants.find((variant) => variant.available) ||
        this.variants[0] ||
        null
      );
    }

    initialBundle() {
      const checked = this.root.querySelector(
        '[data-galaxy-bundle] input:checked, input[data-galaxy-bundle]:checked, [data-galaxy-bundle][data-selected="true"]',
      );
      const quantity = Math.max(
        1,
        Math.round(
          number(
            checked?.dataset.quantity ||
              checked?.closest?.("[data-galaxy-bundle]")?.dataset.quantity ||
              checked?.value ||
              this.quantityInput?.value,
            1,
          ),
        ),
      );
      const one = this.root.querySelector('[data-galaxy-bundle][data-quantity="1"]');
      const source = checked || one;
      return {
        quantity,
        discountPercent: this.discountFor(source),
      };
    }

    discountFor(element) {
      if (!(element instanceof Element)) return 0;
      const card = element.closest("[data-galaxy-bundle]") || element;
      return clamp(
        number(card.dataset.discountPercent || card.dataset.discountPct || card.dataset.percent, 0),
        0,
        100,
      );
    }

    findVariant(value) {
      const id = lastIdPart(value);
      if (id) {
        const byId = this.variants.find((variant) => variant.id === id);
        if (byId) return byId;
      }
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return null;
      return (
        this.variants.find((variant) => variant.title.toLowerCase() === normalized) ||
        this.variants.find((variant) => variant.options.some((option) => option.toLowerCase() === normalized)) ||
        null
      );
    }

    variantFromControl(control) {
      const associatedCard =
        control instanceof HTMLInputElement
          ? [...(control.labels || [])].find((label) => label.matches("[data-galaxy-variant-card]"))
          : null;
      const card = control.closest("[data-galaxy-variant-card]") || associatedCard || control;
      const input = inputForCard(card);
      return this.findVariant(
        card.dataset.variantId ||
          input?.dataset.variantId ||
          (input?.name === "id" ? input.value : "") ||
          card.dataset.variantValue ||
          card.dataset.variantTitle ||
          input?.value,
      );
    }

    bundleFromControl(control) {
      const card = control.closest("[data-galaxy-bundle]") || control;
      const input = card.matches("input") ? card : card.querySelector("input");
      return {
        quantity: Math.max(1, Math.round(number(card.dataset.quantity || input?.value, 1))),
        discountPercent: this.discountFor(card),
      };
    }

    handleRootClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const submit = target.closest("[data-galaxy-sticky-submit], [data-galaxy-final-submit], [data-galaxy-submit]");
      if (submit && this.root.contains(submit)) {
        event.preventDefault();
        this.lastSubmitPlacement =
          submit.dataset.placement ||
          (submit.hasAttribute("data-galaxy-final-submit") ? "final" : "sticky");
        this.requestSubmit();
        return;
      }
      const variantControl = target.closest("[data-galaxy-variant-card]");
      if (variantControl && this.root.contains(variantControl)) {
        event.preventDefault();
        const variant = this.variantFromControl(variantControl);
        if (variant && variant.available) {
          this.selectVariant(variant, "card", true);
          inputForCard(variantControl)?.focus({ preventScroll: true });
        }
        return;
      }

      const bundleControl = target.closest("[data-galaxy-bundle]");
      if (bundleControl && this.root.contains(bundleControl)) {
        event.preventDefault();
        const bundle = this.bundleFromControl(bundleControl);
        this.selectBundle(bundle.quantity, bundle.discountPercent, "card", true);
      }
    }

    handleRootChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (target.closest("[data-galaxy-variant-card]") || target.matches("[data-galaxy-variant-input]")) {
        const variant = this.variantFromControl(target);
        if (variant) this.selectVariant(variant, "control", true);
        return;
      }
      if (target.closest("[data-galaxy-bundle]") || target.matches("[data-galaxy-bundle-input]")) {
        const bundle = this.bundleFromControl(target);
        this.selectBundle(bundle.quantity, bundle.discountPercent, "control", true);
        return;
      }
      if (target === this.quantityInput || target.matches("[data-galaxy-quantity]")) {
        this.selectBundle(Math.max(1, Math.round(number(target.value, 1))), 0, "quantity", true);
      }
    }

    handleDocumentClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const submit = target.closest(
        "[data-galaxy-sticky-submit], [data-galaxy-final-submit], [data-galaxy-submit]",
      );
      if (submit && !this.root.contains(submit) && elementBelongsToProduct(submit, this.productId)) {
        event.preventDefault();
        if (submit.getAttribute("aria-disabled") === "true" || this.addButton?.dataset.state === "loading") {
          return;
        }
        this.lastSubmitPlacement =
          submit.dataset.placement ||
          (submit.hasAttribute("data-galaxy-final-submit") ? "final" : "sticky");
        this.requestSubmit();
        return;
      }

      const variantControl = target.closest(
        "[data-galaxy-select-variant], [data-galaxy-variant-card]",
      );
      if (
        variantControl &&
        !this.root.contains(variantControl) &&
        elementBelongsToProduct(variantControl, this.productId)
      ) {
        event.preventDefault();
        const variant = this.variantFromControl(variantControl);
        if (variant && variant.available) {
          this.selectVariant(variant, "secondary", true);
          this.root.querySelector("[data-galaxy-buybox]")?.scrollIntoView({
            behavior: reducedMotion.matches ? "auto" : "smooth",
            block: "center",
          });
        }
        return;
      }

      const bundleControl = target.closest("[data-galaxy-select-bundle], [data-galaxy-bundle]");
      if (
        bundleControl &&
        !this.root.contains(bundleControl) &&
        elementBelongsToProduct(bundleControl, this.productId)
      ) {
        event.preventDefault();
        const bundle = this.bundleFromControl(bundleControl);
        this.selectBundle(bundle.quantity, bundle.discountPercent, "secondary", true);
      }
    }

    handleStateRequest(event) {
      if (event.detail?.productId && lastIdPart(event.detail.productId) !== this.productId) return;
      this.publishState("request");
    }

    handleExternalVariant(event) {
      if (event.detail?.productId && lastIdPart(event.detail.productId) !== this.productId) return;
      const variant = this.findVariant(event.detail?.variantId || event.detail?.value);
      if (variant && variant.available) this.selectVariant(variant, event.detail?.source || "external", true);
    }

    handleExternalBundle(event) {
      if (event.detail?.productId && lastIdPart(event.detail.productId) !== this.productId) return;
      this.selectBundle(
        Math.max(1, Math.round(number(event.detail?.quantity, 1))),
        clamp(number(event.detail?.discountPercent, 0), 0, 100),
        event.detail?.source || "external",
        true,
      );
    }

    handleSubmit() {
      if (!this.variant?.available) return;
      track("galaxy_add_to_cart_intent", {
        product_id: this.productId,
        variant_id: this.variant.id,
        variant_name: friendlyVariantName(this.variant, this.selectedFriendlyName()),
        quantity: this.quantity,
        placement: this.lastSubmitPlacement,
        bundle_discount_percent: this.discountPercent,
      });
      this.lastSubmitPlacement = "main";
    }

    selectVariant(variant, source, shouldTrack) {
      const changed = this.variant?.id !== variant.id;
      this.variant = variant;
      this.render(true);
      this.publishState(source);
      if (changed) {
        const detail = this.state(source);
        this.root.dispatchEvent(new CustomEvent(EVENT_VARIANT_CHANGE, { bubbles: true, detail }));
        if (shouldTrack) {
          track("galaxy_variant_selected", {
            product_id: this.productId,
            variant_id: variant.id,
            variant_name: detail.variantName,
            source,
          });
        }
      }
    }

    selectBundle(quantity, discountPercent, source, shouldTrack) {
      const changed = this.quantity !== quantity || this.discountPercent !== discountPercent;
      this.quantity = quantity;
      this.discountPercent = discountPercent;
      this.render(false);
      this.publishState(source);
      if (changed) {
        const detail = this.state(source);
        this.root.dispatchEvent(new CustomEvent(EVENT_BUNDLE_CHANGE, { bubbles: true, detail }));
        if (shouldTrack) {
          track("galaxy_bundle_selected", {
            product_id: this.productId,
            variant_id: this.variant?.id || "",
            quantity,
            discount_percent: discountPercent,
            total_price: detail.totalPrice,
            source,
          });
        }
      }
    }

    selectedFriendlyName() {
      if (!this.variant) return "";
      const selected = [...document.querySelectorAll("[data-galaxy-variant-card]")].find(
        (card) =>
          elementBelongsToProduct(card, this.productId) &&
          lastIdPart(card.dataset.variantId || inputForCard(card)?.dataset.variantId || inputForCard(card)?.value) ===
            this.variant.id,
      );
      return (
        selected?.dataset.friendlyName ||
        selected?.querySelector("[data-galaxy-friendly-name], [data-friendly-name], strong")?.textContent ||
        ""
      );
    }

    totalsFor(quantity, discountPercent) {
      const unit = this.variant?.price || 0;
      const compareUnit = this.variant?.compareAtPrice || 0;
      const gross = unit * quantity;
      const discount = Math.floor((gross * discountPercent) / 100);
      const total = Math.max(0, gross - discount);
      const original = compareUnit > unit ? compareUnit * quantity : gross;
      return {
        gross,
        total,
        original,
        savings: Math.max(0, original - total),
        perUnit: quantity > 0 ? Math.round(total / quantity) : total,
      };
    }

    state(source = "state") {
      const totals = this.totalsFor(this.quantity, this.discountPercent);
      const traits = variantTraits(this.variant);
      return {
        productId: this.productId,
        variantId: this.variant?.id || "",
        variantTitle: this.variant?.title || "",
        variantName: this.variant ? friendlyVariantName(this.variant, this.selectedFriendlyName()) : "",
        variantOptions: this.variant?.options || [],
        available: Boolean(this.variant?.available),
        unitPrice: this.variant?.price || 0,
        compareAtPrice: this.variant?.compareAtPrice || 0,
        quantity: this.quantity,
        discountPercent: this.discountPercent,
        grossPrice: totals.gross,
        totalPrice: totals.total,
        originalPrice: totals.original,
        savings: totals.savings,
        perUnitPrice: totals.perUnit,
        mediaId: this.variant?.mediaId || "",
        image: this.variant?.image || null,
        bluetooth: traits.bluetooth,
        color: traits.color,
        version: traits.version,
        source,
      };
    }

    render(updateUrl) {
      if (this.variantInput) this.variantInput.value = this.variant?.id || "";
      if (this.quantityInput) this.quantityInput.value = String(this.quantity);

      this.paintVariantCards();
      this.paintBundleCards();
      const state = this.state("render");
      applyState(state, this.moneyFormat);

      if (this.addButton) {
        const isLoading = this.addButton.dataset.state === "loading";
        this.addButton.dataset.available = String(state.available);
        if (!isLoading) this.addButton.disabled = !state.available;
        const label = this.addButton.querySelector("[data-add-label]");
        if (label && !isLoading) {
          label.textContent = state.available
            ? this.addButton.dataset.idleLabel || "Create my galaxy"
            : this.variant
              ? "Sold out"
              : "Choose a version";
        }
      }

      this.syncSubmitState(this.addButton?.dataset.state || "idle");
      if (updateUrl && this.variant && !isDesignMode()) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", this.variant.id);
        window.history.replaceState({}, "", url);
      }
    }

    paintVariantCards() {
      document.querySelectorAll("[data-galaxy-variant-card]").forEach((card) => {
        if (!elementBelongsToProduct(card, this.productId)) return;
        const variant = this.variantFromControl(card);
        const selected = Boolean(variant && variant.id === this.variant?.id);
        const available = Boolean(variant?.available);
        card.classList.toggle("is-selected", selected);
        card.classList.toggle("is-unavailable", !available);
        card.dataset.selected = String(selected);
        card.dataset.available = String(available);
        card.setAttribute("aria-disabled", String(!available));
        if (card instanceof HTMLButtonElement || ["button", "option", "radio"].includes(card.getAttribute("role"))) {
          card.setAttribute("aria-pressed", String(selected));
        } else {
          card.removeAttribute("aria-pressed");
        }

        const input = inputForCard(card);
        if (input instanceof HTMLInputElement) {
          input.checked = selected;
          input.disabled = !available;
        }
        if (card instanceof HTMLButtonElement) card.disabled = !available;

        const name = card.querySelector("[data-galaxy-friendly-name]");
        if (name && variant && !name.textContent.trim()) {
          name.textContent = friendlyVariantName(variant, card.dataset.friendlyName || "");
        }
        const price = card.querySelector("[data-galaxy-variant-price]");
        if (price && variant) price.textContent = formatMoney(variant.price, this.moneyFormat);
      });
    }

    paintBundleCards() {
      document.querySelectorAll("[data-galaxy-bundle]").forEach((card) => {
        if (!elementBelongsToProduct(card, this.productId)) return;
        const bundle = this.bundleFromControl(card);
        const selected =
          bundle.quantity === this.quantity && bundle.discountPercent === this.discountPercent;
        card.classList.toggle("is-selected", selected);
        card.dataset.selected = String(selected);
        card.setAttribute("aria-pressed", String(selected));
        const input = card.matches("input") ? card : card.querySelector("input");
        if (input instanceof HTMLInputElement) input.checked = selected;

        const totals = this.totalsFor(bundle.quantity, bundle.discountPercent);
        const values = [
          ["[data-galaxy-bundle-total]", totals.total],
          ["[data-galaxy-bundle-original]", totals.original],
          ["[data-galaxy-bundle-saving]", totals.savings],
          ["[data-galaxy-bundle-per-unit]", totals.perUnit],
        ];
        values.forEach(([selector, value]) => {
          const element = card.querySelector(selector);
          if (!element) return;
          element.textContent = formatMoney(value, this.moneyFormat);
          if (selector === "[data-galaxy-bundle-saving]") element.hidden = value <= 0;
        });
      });
    }

    publishState(source) {
      this.root.dispatchEvent(
        new CustomEvent(EVENT_STATE, {
          bubbles: true,
          detail: this.state(source),
        }),
      );
    }

    requestSubmit() {
      if (!this.form || !this.variant?.available) {
        this.root.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "center" });
        return;
      }
      if (this.addButton?.dataset.state === "loading" || this.addButton?.disabled) return;
      if (typeof this.form.requestSubmit === "function") {
        this.form.requestSubmit(this.addButton instanceof HTMLButtonElement ? this.addButton : undefined);
        return;
      }
      if (this.addButton instanceof HTMLButtonElement) this.addButton.click();
    }

    observeButtonState() {
      if (!this.addButton) return;
      this.buttonObserver = new MutationObserver(() => {
        const state = this.addButton?.dataset.state || "idle";
        this.syncSubmitState(state);
        if (state === this.lastButtonState) return;
        this.lastButtonState = state;
        if (state === "success") {
          const detail = this.state("cart-success");
          track("galaxy_add_to_cart_success", {
            product_id: this.productId,
            variant_id: detail.variantId,
            quantity: detail.quantity,
            total_price: detail.totalPrice,
          });
        } else if (state === "error") {
          track("galaxy_add_to_cart_error", {
            product_id: this.productId,
            variant_id: this.variant?.id || "",
            quantity: this.quantity,
          });
        }
      });
      this.buttonObserver.observe(this.addButton, {
        attributes: true,
        attributeFilter: ["data-state", "disabled", "data-available"],
      });
    }

    syncSubmitState(buttonState) {
      const state = this.state("button-state");
      const selectors =
        "[data-galaxy-sticky-submit], [data-galaxy-final-submit], [data-galaxy-submit]";
      document.querySelectorAll(selectors).forEach((button) => {
        if (!elementBelongsToProduct(button, this.productId)) return;
        const loading = buttonState === "loading";
        const disabled = !state.available || loading;
        if (button instanceof HTMLButtonElement) button.disabled = disabled;
        button.setAttribute("aria-disabled", String(disabled));
        button.dataset.state = buttonState;

        const label = textTarget(button);
        if (!label) return;
        if (!button.dataset.idleLabel && label.textContent.trim()) {
          button.dataset.idleLabel = label.textContent.trim();
        }
        if (loading) label.textContent = "Adding…";
        else if (buttonState === "success") label.textContent = "Added";
        else if (buttonState === "error") label.textContent = "Try again";
        else if (!this.variant) label.textContent = "Choose a version";
        else if (!state.available) label.textContent = "Sold out";
        else label.textContent = button.dataset.idleLabel || "Create my galaxy";
      });
    }

    setupStickyBuy() {
      const sticky = this.root.querySelector("[data-sticky-buy]");
      if (!sticky || !this.addButton || !("IntersectionObserver" in window)) return;
      const setShown = (shown) => {
        sticky.classList.toggle("is-visible", shown);
        sticky.setAttribute("aria-hidden", String(!shown));
        if ("inert" in sticky) sticky.inert = !shown;
      };
      setShown(false);
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          setShown(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { threshold: 0.15 },
      );
      this.stickyObserver.observe(this.addButton);
    }
  }

  function appliesToState(element, state) {
    const ids = String(element.dataset.variantIds || element.dataset.variantId || "")
      .split(",")
      .map((id) => lastIdPart(id))
      .filter(Boolean);
    if (ids.length > 0 && !ids.includes(state.variantId)) return false;

    const rule = String(
      element.dataset.galaxyAppliesTo || element.dataset.appliesTo || element.dataset.variantKey || "all",
    ).toLowerCase();
    if (!rule || rule === "all") return true;
    if (rule === "bluetooth") return state.bluetooth;
    if (rule === "standard") return !state.bluetooth;
    if (rule === "black" || rule === "white") return state.color.toLowerCase() === rule;
    return (
      rule === state.variantId.toLowerCase() ||
      state.variantTitle.toLowerCase().includes(rule) ||
      state.variantName.toLowerCase().includes(rule)
    );
  }

  function applyState(state, moneyFormat) {
    const bind = (selector, callback) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (elementBelongsToProduct(element, state.productId)) callback(element);
      });
    };

    bind('[data-galaxy-price="unit"], [data-galaxy-unit-price]', (element) => {
      element.textContent = formatMoney(state.unitPrice, moneyFormat);
    });
    bind("[data-product-price] .price__current", (element) => {
      element.textContent = formatMoney(state.unitPrice, moneyFormat);
    });
    bind("[data-product-price] .price__compare", (element) => {
      element.textContent = formatMoney(state.compareAtPrice, moneyFormat);
      element.hidden = state.compareAtPrice <= state.unitPrice;
    });
    bind("[data-product-price] .price__saving", (element) => {
      const percent =
        state.compareAtPrice > state.unitPrice
          ? Math.round(((state.compareAtPrice - state.unitPrice) / state.compareAtPrice) * 100)
          : 0;
      element.textContent = percent > 0 ? `Save ${percent}%` : "";
      element.hidden = percent <= 0;
    });
    bind('[data-galaxy-price="total"], [data-galaxy-total-price], [data-galaxy-price]:not([data-galaxy-price="unit"])', (element) => {
      element.textContent = formatMoney(state.totalPrice, moneyFormat);
    });
    bind("[data-galaxy-per-unit-price]", (element) => {
      element.textContent = formatMoney(state.perUnitPrice, moneyFormat);
    });
    bind("[data-galaxy-compare-price]", (element) => {
      element.textContent = formatMoney(state.originalPrice, moneyFormat);
      element.hidden = state.originalPrice <= state.totalPrice;
    });
    bind("[data-galaxy-savings]", (element) => {
      element.textContent = formatMoney(state.savings, moneyFormat);
      element.hidden = state.savings <= 0;
    });
    bind("[data-galaxy-quantity-value]", (element) => {
      element.textContent = String(state.quantity);
    });
    bind("[data-galaxy-variant-name], [data-selected-option-label], [data-sticky-variant]", (element) => {
      element.textContent = state.variantName;
    });
    bind("[data-galaxy-variant-title]", (element) => {
      element.textContent = state.variantTitle;
    });
    bind("[data-galaxy-color]", (element) => {
      element.textContent = state.color;
    });
    bind("[data-galaxy-availability]", (element) => {
      element.textContent = state.available ? element.dataset.availableLabel || "Available" : "Sold out";
      element.dataset.available = String(state.available);
    });
    bind("[data-galaxy-submit-price], [data-sticky-price], [data-add-price]", (element) => {
      element.textContent = formatMoney(state.totalPrice, moneyFormat);
    });
    bind("[data-galaxy-variant-image]", (element) => {
      if (!(element instanceof HTMLImageElement) || !state.image?.src) return;
      if (element.src !== state.image.src) element.src = state.image.src;
      if (state.image.alt) element.alt = state.image.alt;
    });
    bind("[data-sticky-product-image]", (element) => {
      if (!(element instanceof HTMLImageElement)) return;
      const source = state.color === "White" ? element.dataset.whiteSrc : element.dataset.blackSrc;
      if (source && element.src !== source) element.src = source;
    });
    bind("[data-galaxy-variant-content], [data-galaxy-applies-to]", (element) => {
      setVisible(element, appliesToState(element, state));
    });
  }

  class GalaxyGalleryController {
    constructor(root) {
      this.root = root;
      this.productId = productIdFor(root);
      this.slides = [...root.querySelectorAll("[data-galaxy-slide], [data-gallery-slide]")];
      this.thumbs = [...root.querySelectorAll("[data-galaxy-thumb], [data-gallery-thumb]")];
      this.track =
        root.querySelector("[data-galaxy-track], [data-gallery-track]") ||
        root.querySelector("[data-galaxy-stage], [data-gallery-stage]") ||
        root;
      this.lightbox = root.querySelector("dialog[data-product-lightbox], [data-product-lightbox]");
      this.lightboxContent = root.querySelector("[data-lightbox-content]");
      this.lightboxTrigger = null;
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains("is-active") || slide.getAttribute("aria-hidden") === "false"),
      );
      this.pointer = null;
      this.suppressClickUntil = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleState = this.handleState.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleLightboxClose = this.handleLightboxClose.bind(this);
    }

    connect() {
      if (this.root.dataset.galaxyGalleryReady === "true") return;
      this.root.dataset.galaxyGalleryReady = "true";
      this.root.addEventListener("click", this.handleClick);
      this.track.addEventListener("pointerdown", this.handlePointerDown);
      this.track.addEventListener("pointermove", this.handlePointerMove);
      this.track.addEventListener("pointerup", this.handlePointerUp);
      this.track.addEventListener("pointercancel", this.handlePointerUp);
      this.track.style.touchAction = "pan-y";
      this.lightbox?.addEventListener("close", this.handleLightboxClose);
      document.addEventListener(EVENT_STATE, this.handleState);
      document.addEventListener("shopify:block:select", this.handleBlockSelect);
      this.select(this.index, "initial", false);
      document.dispatchEvent(new CustomEvent(EVENT_STATE_REQUEST, { detail: { productId: this.productId } }));
    }

    disconnect() {
      this.root.removeEventListener("click", this.handleClick);
      this.track.removeEventListener("pointerdown", this.handlePointerDown);
      this.track.removeEventListener("pointermove", this.handlePointerMove);
      this.track.removeEventListener("pointerup", this.handlePointerUp);
      this.track.removeEventListener("pointercancel", this.handlePointerUp);
      document.removeEventListener(EVENT_STATE, this.handleState);
      document.removeEventListener("shopify:block:select", this.handleBlockSelect);
      this.lightbox?.removeEventListener("close", this.handleLightboxClose);
      this.root.removeAttribute("data-galaxy-gallery-ready");
    }

    handleClick(event) {
      if (Date.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) return;
      const close = target.closest("[data-lightbox-close]");
      if (close && this.root.contains(close)) {
        event.preventDefault();
        this.closeLightbox();
        return;
      }
      const zoom = target.closest("[data-gallery-zoom]");
      if (zoom && this.root.contains(zoom)) {
        event.preventDefault();
        this.openLightbox(zoom);
        return;
      }
      if (target.closest("[data-galaxy-prev], [data-gallery-prev]")) {
        event.preventDefault();
        this.select(this.index - 1, "previous", true);
        return;
      }
      if (target.closest("[data-galaxy-next], [data-gallery-next]")) {
        event.preventDefault();
        this.select(this.index + 1, "next", true);
        return;
      }
      const thumb = target.closest("[data-galaxy-thumb], [data-gallery-thumb]");
      if (!thumb) return;
      event.preventDefault();
      const index = this.thumbs.indexOf(thumb);
      if (index >= 0) this.select(index, "thumbnail", true);
    }

    handlePointerDown(event) {
      if (event.pointerType === "mouse" || !event.isPrimary || this.pointer) return;
      const interactive = event.target.closest("button, a, input, select, textarea, video, model-viewer");
      if (interactive && !interactive.matches("[data-gallery-zoom]")) return;
      this.pointer = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
        horizontal: false,
      };
      try {
        this.track.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is an enhancement, not a requirement.
      }
    }

    handlePointerMove(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      if (!this.pointer.horizontal && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
        this.pointer.horizontal = true;
      }
      if (this.pointer.horizontal) event.preventDefault();
    }

    handlePointerUp(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const pointer = this.pointer;
      this.pointer = null;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      const elapsed = Math.max(1, performance.now() - pointer.time);
      const velocity = Math.abs(dx) / elapsed;
      try {
        this.track.releasePointerCapture(event.pointerId);
      } catch {
        // The browser may already have released capture.
      }
      if (!pointer.horizontal || Math.abs(dx) <= Math.abs(dy)) return;
      if (Math.abs(dx) >= 44 || velocity > 0.45) {
        this.suppressClickUntil = Date.now() + 300;
        this.select(this.index + (dx < 0 ? 1 : -1), "swipe", true);
      }
    }

    handleState(event) {
      const detail = event.detail;
      if (!detail || (this.productId && detail.productId !== this.productId)) return;
      const curatedMediaId =
        detail.color === "White"
          ? "studio-galaxy-white"
          : detail.color === "Black"
            ? "studio-galaxy-black"
            : "";
      if (curatedMediaId) {
        this.selectMedia(curatedMediaId, "variant");
        return;
      }
      const shouldFollowVariantMedia = !["initial", "request"].includes(detail.source);
      if (detail.mediaId && shouldFollowVariantMedia) this.selectMedia(detail.mediaId, "variant");
    }

    handleBlockSelect(event) {
      const slide = event.target?.closest?.("[data-galaxy-slide], [data-gallery-slide]");
      if (!slide || !this.root.contains(slide)) return;
      const index = this.slides.indexOf(slide);
      if (index >= 0) this.select(index, "editor", false);
    }

    openLightbox(trigger) {
      if (!(this.lightbox instanceof HTMLDialogElement) || !this.lightboxContent) return;
      const image = this.slides[this.index]?.querySelector("img");
      if (!(image instanceof HTMLImageElement)) return;
      const clone = image.cloneNode(true);
      clone.removeAttribute("loading");
      clone.removeAttribute("fetchpriority");
      clone.sizes = "100vw";
      this.lightboxContent.replaceChildren(clone);
      this.lightboxTrigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
      if (!this.lightbox.open) this.lightbox.showModal();
      this.lightbox.querySelector("[data-lightbox-close]")?.focus({ preventScroll: true });
      track("galaxy_media_zoomed", {
        product_id: this.productId,
        media_id: lastIdPart(this.slides[this.index]?.dataset.mediaId),
        media_index: this.index + 1,
      });
    }

    closeLightbox() {
      if (this.lightbox instanceof HTMLDialogElement && this.lightbox.open) this.lightbox.close();
    }

    handleLightboxClose() {
      if (this.lightboxTrigger instanceof HTMLElement && this.lightboxTrigger.isConnected) {
        this.lightboxTrigger.focus({ preventScroll: true });
      }
      this.lightboxTrigger = null;
    }

    selectMedia(mediaId, source = "variant") {
      const normalized = lastIdPart(mediaId);
      const index = this.slides.findIndex(
        (slide) => lastIdPart(slide.dataset.mediaId || slide.dataset.variantMediaId) === normalized,
      );
      if (index >= 0) this.select(index, source, false);
    }

    select(rawIndex, source, shouldTrack) {
      if (this.slides.length === 0) return;
      const next = (rawIndex + this.slides.length) % this.slides.length;
      this.index = next;
      this.slides.forEach((slide, index) => {
        const active = index === next;
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", String(!active));
        if ("inert" in slide) slide.inert = !active;
        if (!active) slide.querySelectorAll("video").forEach((video) => video.pause());
      });
      this.thumbs.forEach((thumb, index) => {
        const active = index === next;
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-current", String(active));
        thumb.setAttribute("aria-pressed", String(active));
        if (active) {
          thumb.scrollIntoView({
            block: "nearest",
            inline: "nearest",
            behavior: reducedMotion.matches || source === "editor" ? "auto" : "smooth",
          });
        }
      });
      this.root.querySelectorAll("[data-galaxy-index], [data-gallery-index]").forEach((counter) => {
        counter.textContent = String(next + 1);
      });
      this.root.querySelectorAll("[data-galaxy-total], [data-gallery-total]").forEach((counter) => {
        counter.textContent = String(this.slides.length);
      });
      if (shouldTrack) {
        const slide = this.slides[next];
        track("galaxy_media_interaction", {
          product_id: this.productId,
          media_id: lastIdPart(slide?.dataset.mediaId),
          media_index: next + 1,
          media_type: slide?.dataset.mediaType || (slide?.querySelector("video") ? "video" : "image"),
          source,
        });
      }
    }
  }

  class BeforeAfterController {
    constructor(root) {
      this.root = root;
      this.productId = productIdFor(root);
      this.range =
        root.querySelector("[data-before-after-range]") || root.querySelector('input[type="range"]');
      this.overlay = root.querySelector("[data-before-after-after], [data-before-after-overlay]");
      this.handleInput = this.handleInput.bind(this);
      this.handleChange = this.handleChange.bind(this);
    }

    connect() {
      if (!this.range || this.root.dataset.beforeAfterReady === "true") return;
      this.root.dataset.beforeAfterReady = "true";
      this.range.addEventListener("input", this.handleInput);
      this.range.addEventListener("change", this.handleChange);
      this.paint();
    }

    disconnect() {
      this.range?.removeEventListener("input", this.handleInput);
      this.range?.removeEventListener("change", this.handleChange);
      this.root.removeAttribute("data-before-after-ready");
    }

    value() {
      const min = number(this.range?.min, 0);
      const max = number(this.range?.max, 100);
      const current = clamp(number(this.range?.value, 50), min, max);
      return max === min ? 50 : ((current - min) / (max - min)) * 100;
    }

    paint() {
      const value = this.value();
      this.root.style.setProperty("--before-after-position", `${value}%`);
      if (this.overlay) this.overlay.style.clipPath = `inset(0 0 0 ${value}%)`;
      this.root.querySelectorAll("[data-before-after-value]").forEach((output) => {
        output.textContent = `${Math.round(value)}%`;
      });
    }

    handleInput() {
      this.paint();
    }

    handleChange() {
      this.paint();
      track("galaxy_before_after_used", {
        product_id: this.productId,
        position: Math.round(this.value()),
      });
    }
  }

  class GalaxyScenesController {
    constructor(root) {
      this.root = root;
      this.productId = productIdFor(root);
      this.controls = [
        ...root.querySelectorAll("[data-galaxy-scene], [data-scene-tab], [data-galaxy-tab]"),
      ];
      this.panels = [
        ...root.querySelectorAll("[data-galaxy-scene-panel], [data-scene-panel], [data-galaxy-tab-panel]"),
      ];
      this.index = Math.max(
        0,
        this.controls.findIndex(
          (control) => control.getAttribute("aria-selected") === "true" || control.classList.contains("is-active"),
        ),
      );
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connect() {
      if (this.controls.length === 0 || this.root.dataset.galaxyScenesReady === "true") return;
      this.root.dataset.galaxyScenesReady = "true";
      this.root.addEventListener("click", this.handleClick);
      this.root.addEventListener("keydown", this.handleKeydown);
      document.addEventListener("shopify:block:select", this.handleBlockSelect);
      const list = this.controls[0]?.parentElement;
      if (list && !list.hasAttribute("role")) list.setAttribute("role", "tablist");
      this.select(this.index, "initial", false, false);
    }

    disconnect() {
      this.root.removeEventListener("click", this.handleClick);
      this.root.removeEventListener("keydown", this.handleKeydown);
      document.removeEventListener("shopify:block:select", this.handleBlockSelect);
      this.root.removeAttribute("data-galaxy-scenes-ready");
    }

    keyFor(control, index) {
      return String(
        control.dataset.sceneId ||
          control.dataset.scene ||
          control.dataset.tab ||
          control.getAttribute("aria-controls") ||
          index,
      );
    }

    panelFor(control, index) {
      const key = this.keyFor(control, index);
      return (
        this.panels.find(
          (panel, panelIndex) =>
            String(
              panel.dataset.sceneId || panel.dataset.scene || panel.dataset.tab || panel.id || panelIndex,
            ) === key,
        ) || this.panels[index]
      );
    }

    handleClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const control = target.closest("[data-galaxy-scene], [data-scene-tab], [data-galaxy-tab]");
      if (!control || !this.root.contains(control)) return;
      event.preventDefault();
      const index = this.controls.indexOf(control);
      if (index >= 0) this.select(index, "click", true, false);
    }

    handleKeydown(event) {
      const control = event.target.closest?.(
        "[data-galaxy-scene], [data-scene-tab], [data-galaxy-tab]",
      );
      if (!control) return;
      const current = this.controls.indexOf(control);
      if (current < 0) return;
      let next = null;
      if (event.key === "ArrowRight" || event.key === "ArrowDown") next = current + 1;
      else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = current - 1;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = this.controls.length - 1;
      if (next === null) return;
      event.preventDefault();
      next = (next + this.controls.length) % this.controls.length;
      this.select(next, "keyboard", true, true);
    }

    handleBlockSelect(event) {
      const block = event.target;
      if (!(block instanceof Element) || !this.root.contains(block)) return;
      const control = block.closest("[data-galaxy-scene], [data-scene-tab], [data-galaxy-tab]");
      let index = control ? this.controls.indexOf(control) : -1;
      if (index < 0) {
        const panel = block.closest(
          "[data-galaxy-scene-panel], [data-scene-panel], [data-galaxy-tab-panel]",
        );
        index = panel ? this.panels.indexOf(panel) : -1;
      }
      if (index >= 0) this.select(index, "editor", false, false);
    }

    select(rawIndex, source, shouldTrack, shouldFocus) {
      if (this.controls.length === 0) return;
      const index = (rawIndex + this.controls.length) % this.controls.length;
      this.index = index;
      this.controls.forEach((control, controlIndex) => {
        const active = controlIndex === index;
        const panel = this.panelFor(control, controlIndex);
        control.setAttribute("role", "tab");
        control.setAttribute("aria-selected", String(active));
        control.setAttribute("tabindex", active ? "0" : "-1");
        control.classList.toggle("is-active", active);
        if (panel) {
          if (!panel.id) panel.id = `GalaxyPanel-${this.productId || "product"}-${controlIndex}`;
          control.setAttribute("aria-controls", panel.id);
        }
      });
      this.panels.forEach((panel, panelIndex) => {
        const activePanel = this.panelFor(this.controls[index], index);
        const active = panel === activePanel || (activePanel === undefined && panelIndex === index);
        panel.setAttribute("role", "tabpanel");
        setVisible(panel, active);
      });
      if (shouldFocus) this.controls[index]?.focus({ preventScroll: true });
      if (shouldTrack) {
        const control = this.controls[index];
        track("galaxy_scene_selected", {
          product_id: this.productId,
          scene_id: this.keyFor(control, index),
          scene_name: control.dataset.sceneName || control.textContent.trim(),
          source,
        });
      }
    }
  }

  let revealObserver = null;
  function sharedRevealObserver() {
    if (revealObserver || reducedMotion.matches || isDesignMode() || !("IntersectionObserver" in window)) {
      return revealObserver;
    }
    revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealElement(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );
    return revealObserver;
  }

  function revealElement(element) {
    element.classList.add("is-visible", "is-revealed");
    element.dataset.revealed = "true";
  }

  class GalaxyRevealController {
    constructor(root) {
      this.root = root;
    }

    connect() {
      if (this.root.dataset.galaxyRevealReady === "true") return;
      this.root.dataset.galaxyRevealReady = "true";
      if (reducedMotion.matches || isDesignMode() || !("IntersectionObserver" in window)) {
        revealElement(this.root);
        return;
      }
      sharedRevealObserver()?.observe(this.root);
    }

    disconnect() {
      revealObserver?.unobserve(this.root);
      this.root.removeAttribute("data-galaxy-reveal-ready");
    }
  }

  function bindFaq(scope) {
    allWithin(scope, "[data-galaxy-faq] details, .galaxy-faq details, #FAQ details").forEach((details) => {
      if (faqBindings.has(details)) return;
      faqBindings.add(details);
      details.addEventListener("toggle", () => {
        if (!details.open) return;
        const summary = details.querySelector("summary");
        track("galaxy_faq_opened", {
          product_id: productIdFor(details) || firstProductId(),
          faq_id: details.dataset.faqId || details.id || "",
          question: summary?.textContent?.trim() || "",
        });
      });
    });
  }

  function bindVideos(scope) {
    allWithin(
      scope,
      "[data-galaxy-demo] video, .galaxy-demo video, [data-galaxy-gallery] video, studio-galaxy-gallery video, studio-galaxy-product video",
    ).forEach((video) => {
      if (videoBindings.has(video)) return;
      videoBindings.add(video);
      video.addEventListener("play", () => {
        if (trackedVideos.has(video)) return;
        trackedVideos.add(video);
        track("galaxy_video_played", {
          product_id: productIdFor(video) || firstProductId(),
          video_id: video.dataset.videoId || video.id || "",
          placement: video.closest("[data-galaxy-demo], .galaxy-demo") ? "demonstration" : "gallery",
        });
      });
    });
  }

  function firstProductId() {
    for (const controller of productControllers) {
      if (controller.root.isConnected) return controller.productId;
    }
    return "";
  }

  function enhanceProduct(element) {
    if (element.__galaxyProductController) return element.__galaxyProductController;
    const controller = new GalaxyProductController(element);
    element.__galaxyProductController = controller;
    productControllers.add(controller);
    controller.connect();
    return controller;
  }

  function enhanceGallery(element) {
    if (element.__galaxyGalleryController) return element.__galaxyGalleryController;
    const controller = new GalaxyGalleryController(element);
    element.__galaxyGalleryController = controller;
    galleryControllers.add(controller);
    controller.connect();
    return controller;
  }

  function enhanceBeforeAfter(element) {
    if (element.__beforeAfterController) return element.__beforeAfterController;
    const controller = new BeforeAfterController(element);
    element.__beforeAfterController = controller;
    beforeAfterControllers.add(controller);
    controller.connect();
    return controller;
  }

  function enhanceScenes(element) {
    if (element.__galaxyScenesController) return element.__galaxyScenesController;
    const controller = new GalaxyScenesController(element);
    element.__galaxyScenesController = controller;
    sceneControllers.add(controller);
    controller.connect();
    return controller;
  }

  function enhanceReveal(element) {
    if (element.__galaxyRevealController) return element.__galaxyRevealController;
    const controller = new GalaxyRevealController(element);
    element.__galaxyRevealController = controller;
    revealControllers.add(controller);
    controller.connect();
    return controller;
  }

  function cleanDisconnectedControllers() {
    [productControllers, galleryControllers, sceneControllers, beforeAfterControllers, revealControllers].forEach(
      (controllers) => {
        controllers.forEach((controller) => {
          if (controller.root.isConnected) return;
          controller.disconnect?.();
          controllers.delete(controller);
        });
      },
    );
  }

  function boot(scope = document) {
    cleanDisconnectedControllers();
    allWithin(scope, "studio-galaxy-product, [data-galaxy-product]").forEach(enhanceProduct);
    allWithin(scope, "studio-galaxy-gallery, [data-galaxy-gallery]").forEach(enhanceGallery);
    allWithin(
      scope,
      "studio-before-after, studio-galaxy-before-after, [data-galaxy-before-after]",
    ).forEach(enhanceBeforeAfter);
    allWithin(
      scope,
      "studio-galaxy-scenes, studio-galaxy-tabs, [data-galaxy-scenes], [data-galaxy-tabs]",
    ).forEach(enhanceScenes);
    allWithin(scope, "studio-galaxy-reveal, [data-galaxy-reveal]").forEach(enhanceReveal);
    bindFaq(scope);
    bindVideos(scope);
    if (scope !== document) {
      document.dispatchEvent(new CustomEvent(EVENT_STATE_REQUEST, { detail: {} }));
    }
  }

  class StudioGalaxyProductElement extends HTMLElement {
    connectedCallback() {
      enhanceProduct(this);
    }

    disconnectedCallback() {
      this.__galaxyProductController?.disconnect();
      productControllers.delete(this.__galaxyProductController);
      this.__galaxyProductController = null;
    }
  }

  class StudioGalaxyGalleryElement extends HTMLElement {
    connectedCallback() {
      enhanceGallery(this);
    }

    disconnectedCallback() {
      this.__galaxyGalleryController?.disconnect();
      galleryControllers.delete(this.__galaxyGalleryController);
      this.__galaxyGalleryController = null;
    }

    selectMedia(mediaId) {
      this.__galaxyGalleryController?.selectMedia(mediaId);
    }
  }

  class StudioBeforeAfterElement extends HTMLElement {
    connectedCallback() {
      enhanceBeforeAfter(this);
    }

    disconnectedCallback() {
      this.__beforeAfterController?.disconnect();
      beforeAfterControllers.delete(this.__beforeAfterController);
      this.__beforeAfterController = null;
    }
  }

  class StudioGalaxyBeforeAfterElement extends StudioBeforeAfterElement {}

  class StudioGalaxyScenesElement extends HTMLElement {
    connectedCallback() {
      enhanceScenes(this);
    }

    disconnectedCallback() {
      this.__galaxyScenesController?.disconnect();
      sceneControllers.delete(this.__galaxyScenesController);
      this.__galaxyScenesController = null;
    }
  }

  class StudioGalaxyTabsElement extends StudioGalaxyScenesElement {}

  class StudioGalaxyRevealElement extends HTMLElement {
    connectedCallback() {
      enhanceReveal(this);
    }

    disconnectedCallback() {
      this.__galaxyRevealController?.disconnect();
      revealControllers.delete(this.__galaxyRevealController);
      this.__galaxyRevealController = null;
    }
  }

  const definitions = [
    ["studio-galaxy-product", StudioGalaxyProductElement],
    ["studio-galaxy-gallery", StudioGalaxyGalleryElement],
    ["studio-before-after", StudioBeforeAfterElement],
    ["studio-galaxy-before-after", StudioGalaxyBeforeAfterElement],
    ["studio-galaxy-scenes", StudioGalaxyScenesElement],
    ["studio-galaxy-tabs", StudioGalaxyTabsElement],
    ["studio-galaxy-reveal", StudioGalaxyRevealElement],
  ];
  definitions.forEach(([name, constructor]) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  });

  document.addEventListener("shopify:section:load", (event) => boot(event.target));
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document), { once: true });
  } else {
    boot(document);
  }

  window[RUNTIME_KEY] = { boot, track, version: "1.0.0" };
})();
